import type { SiteRagReport } from '../types/siteRag';
import { stripTemplateLinkedLayoutSnapshots } from './stripTemplateLinkedLayoutSnapshots';

/** True when the in-memory row carries a full GCS body safe to write back. */
export function isReportHydratedForGcsSave(report: SiteRagReport): boolean {
  if ((report as { _metadataOnly?: boolean })._metadataOnly === true) return false;
  if ((report as { _portfolioSlimBody?: boolean })._portfolioSlimBody === true) return false;
  return report.datasets != null;
}

/**
 * Prepare a report for POST /api/storage/save. Strips gen-time layout
 * snapshots for template-linked reports and omits undefined keys (no
 * `undefined → null` conversion that would persist stale snapshots).
 *
 * Also strips all TRANSIENT in-memory annotations:
 *   - `_metadataOnly` — hydration hint, must not reach GCS
 *   - `_portfolioSlimBody` — Phase-2 slim body hint, must not reach GCS
 *   - `_ownerUid`     — #owner-path-sweep-p2 transient, must not be persisted
 *                       (callers MUST also call `stripTransientReportAnnotations`
 *                       before this; the strip here is defence-in-depth)
 */
export function serializeReportForGcsSave(report: SiteRagReport): Record<string, unknown> {
  const stripped = stripTemplateLinkedLayoutSnapshots({ ...report });
  const {
    _metadataOnly: _omit,
    _portfolioSlimBody: _omitSlim,
    _ownerUid: _omitOwner,
    _frozenBranding: _omitFrozenBranding,
    ...withoutInternal
  } = stripped as SiteRagReport & {
    _metadataOnly?: boolean;
    _portfolioSlimBody?: boolean;
  };
  void _omit;
  void _omitSlim;
  void _omitOwner;
  void _omitFrozenBranding;
  return JSON.parse(JSON.stringify(withoutInternal)) as Record<string, unknown>;
}
